/**
 * addressCorrectionQueries - React Query hooks for address correction operations
 *
 * SESSION 197: Address Correction Frontend Admin UI
 *
 * Provides hooks for:
 * - Fetching address corrections for review (query)
 * - Approving corrections (mutation)
 * - Rejecting corrections (mutation)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { addressCorrectionApi } from '@/infrastructure/api/AddressCorrectionApi';
import { notificationService } from '@/infrastructure/notifications';
import type {
  GetAddressCorrectionsParams,
  GetAddressCorrectionsResponse,
  ApproveAddressCorrectionCommand,
  RejectAddressCorrectionCommand
} from '@/domain/entities/AddressCorrection';

/**
 * Query keys for address corrections
 */
export const addressCorrectionKeys = {
  all: ['address-corrections'] as const,
  lists: () => [...addressCorrectionKeys.all, 'list'] as const,
  list: (params?: GetAddressCorrectionsParams) => [...addressCorrectionKeys.lists(), { params }] as const
};

/**
 * useAddressCorrections - Query hook for fetching address corrections
 *
 * Features:
 * - Automatic refetch on window focus
 * - Cache management
 * - Error handling with toast notifications
 *
 * @param params - Query parameters (page, pageSize, status filter)
 * @param options - Query options (e.g., { enabled: boolean })
 * @returns React Query result with paged address corrections data
 *
 * @example
 * const { data, isLoading, error } = useAddressCorrections({
 *   page: 1,
 *   pageSize: 25,
 *   status: 'Pending'
 * }, { enabled: true });
 */
export function useAddressCorrections(
  params?: GetAddressCorrectionsParams,
  options?: { enabled?: boolean }
) {
  return useQuery<GetAddressCorrectionsResponse, Error>({
    queryKey: addressCorrectionKeys.list(params),
    queryFn: () => addressCorrectionApi.getAddressCorrections(params),
    enabled: options?.enabled ?? true,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });
}

/**
 * useApproveAddressCorrection - Mutation hook for approving an address correction
 *
 * Features:
 * - Automatic query invalidation
 * - Success/error toast notifications
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const approveMutation = useApproveAddressCorrection();
 *
 * await approveMutation.mutateAsync({
 *   id: '123e4567-e89b-12d3-a456-426614174000',
 *   command: { adminNotes: 'Address verified' }
 * });
 */
export function useApproveAddressCorrection() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { id: string; command?: ApproveAddressCorrectionCommand }
  >({
    mutationFn: async ({ id, command }) => {
      await addressCorrectionApi.approveCorrection(id, command);
    },
    onSuccess: () => {
      // Invalidate all address correction queries to refetch data
      void queryClient.invalidateQueries({
        queryKey: addressCorrectionKeys.lists()
      });

      // Show success notification
      notificationService.showSuccess('Address correction approved successfully');
    },
    onError: (error: Error) => {
      // Error notification is already shown by ApiClient
      console.error('Failed to approve address correction:', error);
    }
  });
}

/**
 * useRejectAddressCorrection - Mutation hook for rejecting an address correction
 *
 * Features:
 * - Automatic query invalidation
 * - Success/error toast notifications
 *
 * @returns Mutation object with mutate/mutateAsync functions
 *
 * @example
 * const rejectMutation = useRejectAddressCorrection();
 *
 * await rejectMutation.mutateAsync({
 *   id: '123e4567-e89b-12d3-a456-426614174000',
 *   command: {
 *     rejectionReason: 'Address could not be verified',
 *     adminNotes: 'Please provide a valid address'
 *   }
 * });
 */
export function useRejectAddressCorrection() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { id: string; command: RejectAddressCorrectionCommand }
  >({
    mutationFn: async ({ id, command }) => {
      await addressCorrectionApi.rejectCorrection(id, command);
    },
    onSuccess: () => {
      // Invalidate all address correction queries to refetch data
      void queryClient.invalidateQueries({
        queryKey: addressCorrectionKeys.lists()
      });

      // Show success notification
      notificationService.showSuccess('Address correction rejected');
    },
    onError: (error: Error) => {
      // Error notification is already shown by ApiClient
      console.error('Failed to reject address correction:', error);
    }
  });
}
